import { isEqual } from "@common/helpers/comparison";
import type { View } from "@common/types/views/views";

/**
 * `view` with each column swapped for its entry in `savedColumns`, the view
 * as saved. `useTaskViewState` re-adds the Client column to the list's view
 * without its options, so without this a Client resize snaps back on drop.
 */
export const withSavedColumns = <TView extends View>({
    view,
    savedColumns,
}: {
    view: TView;
    savedColumns: View["columns"];
}): TView => {
    const savedById = new Map(
        savedColumns.map((column) => [column.id, column]),
    );
    const columns = view.columns.map(
        (column) => savedById.get(column.id) ?? column,
    );
    return columns.every((column, index) => column === view.columns[index])
        ? view
        : { ...view, columns };
};

/**
 * The saved view once the list changed `listView` into `nextListView`. The
 * list writes only columns and density, so only those carry over: what
 * `useTaskViewState` adds for the list (the Client column, `bulkActions`)
 * stays out of the saved view unless the user changed it, as a resized
 * Client column. Returns `savedView` itself when nothing changed.
 */
export const toSavedView = <TView extends View>({
    savedView,
    listView,
    nextListView,
}: {
    savedView: TView;
    listView: View;
    nextListView: View;
}): TView => {
    const savedIds = new Set(savedView.columns.map((column) => column.id));
    const listById = new Map(
        listView.columns.map((column) => [column.id, column]),
    );
    const columns = nextListView.columns.filter(
        (column) =>
            savedIds.has(column.id) ||
            !isEqual(column, listById.get(column.id)),
    );
    const density = nextListView.options?.density;
    const options =
        density === listView.options?.density
            ? savedView.options
            : { ...savedView.options, density };
    return isEqual(columns, savedView.columns) && options === savedView.options
        ? savedView
        : { ...savedView, columns, options };
};

/**
 * `view` with the picked view's saved filters back, keeping its columns,
 * options, and sort: what Clear filters selects, so only the filters the
 * user added go.
 */
export const withBaseViewFilters = <TView extends View>({
    view,
    baseView,
}: {
    view: TView;
    baseView: View | null;
}): TView => ({
    ...view,
    filters: {
        ...baseView?.filters,
        taskSort: view.filters.taskSort,
    },
});
